import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { StatusBadge } from '../components/ui/StatusBadge';
import { Filter } from 'lucide-react';
import { EmptyState } from '../components/EmptyState';
import { PageHeader } from '../components/PageHeader';
import { MockBadge } from '../components/MockBadge';
import { Sheet, SheetContent, SheetBody, SheetFooter } from '../components/ui/Sheet';
import { CheckboxGroup } from '../components/AudiencePicker';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate, formatNumber } from '../lib/format';
import { parseCodeList } from '../lib/contactAttributes';
import { AUTO_LABEL_NAMES } from '../lib/automationLabels';
import { apiErrorMessage } from './ListerPage';
import type {
  ChurnRisk,
  AutoLabel,
  LabelsOverviewResponse,
  Segment,
  SegmentConsentCategory,
  SegmentEngagement,
  SegmentEstimate,
  SegmentEventCondition,
  SegmentFilter,
  SegmentGroup,
  SegmentLibraryResponse,
  SegmentPredictionCondition,
  SegmentPurchaseCondition,
} from '../types/api';

const EMPTY_GROUP: SegmentGroup = {
  labels: [],
  excludeLabels: [],
  fields: [],
  unsubscribed: false,
  sources: [],
  languages: [],
  countries: [],
  excludeCountries: [],
  engagement: null,
};

const EMPTY_FILTER: SegmentFilter = { version: 2, groups: [EMPTY_GROUP] };

/** Same caps as the server (`segments.repo.ts`, `segmentConditions.ts`). */
const MAX_GROUPS = 5;
const MAX_CONDITIONS = 20;
const MAX_HEAVY_CONDITIONS = 6;

const CONSENT_OPTIONS: { value: SegmentConsentCategory; label: string }[] = [
  { value: 'confirmed_double_optin', label: 'Confirmed (double opt-in)' },
  { value: 'single_optin_with_time_and_ip', label: 'Single opt-in with time and IP' },
  { value: 'time_only', label: 'Time only' },
  { value: 'no_evidence', label: 'No evidence' },
];

/** Mirrors the server's event registry (`EVENT_DEFS`). `presence`: only "at least once" / "never". */
const EVENT_OPTIONS: {
  value: string;
  label: string;
  maxDays: number;
  optionalWindow?: boolean;
  presence?: boolean;
  product?: boolean;
}[] = [
  { value: 'clicked', label: 'Clicked a mail', maxDays: 730 },
  { value: 'opened', label: 'Opened a mail', maxDays: 90 },
  { value: 'received', label: 'Received a mail', maxDays: 90 },
  {
    value: 'ordered',
    label: 'Placed an order',
    maxDays: 3650,
    optionalWindow: true,
    product: true,
  },
  { value: 'signed_up', label: 'Signed up', maxDays: 3650, presence: true },
  { value: 'cart_abandoned', label: 'Left a cart', maxDays: 365 },
];

const ENGAGEMENT_DAYS_MAX = 730;
const LIVE_MAIL_DAYS = 90;

interface SegmentDraft {
  name: string;
  filter: SegmentFilter;
}

function groupConditionCount(group: SegmentGroup): number {
  return (
    group.labels.length +
    group.excludeLabels.length +
    group.fields.length +
    (group.unsubscribed ? 1 : 0) +
    (group.sources.length > 0 ? 1 : 0) +
    (group.languages.length > 0 ? 1 : 0) +
    (group.countries.length > 0 ? 1 : 0) +
    (group.excludeCountries.length > 0 ? 1 : 0) +
    (group.engagement ? 1 : 0) +
    ((group.consentCategories?.length ?? 0) > 0 ? 1 : 0) +
    (group.events?.length ?? 0) +
    (group.purchases?.length ?? 0) +
    (group.predictions?.length ?? 0)
  );
}

function conditionCount(filter: SegmentFilter): number {
  return filter.groups.reduce((sum, g) => sum + groupConditionCount(g), 0);
}

function heavyCount(filter: SegmentFilter): number {
  return filter.groups.reduce(
    (sum, g) =>
      sum + (g.events?.length ?? 0) + (g.purchases?.length ?? 0) + (g.predictions?.length ?? 0),
    0,
  );
}

/** Accepts the old flat shape (from `?prefill` links) and v2. */
function toFilterV2(input: unknown): SegmentFilter {
  const raw = (typeof input === 'object' && input !== null ? input : {}) as Record<string, unknown>;
  if (Array.isArray(raw.groups) && raw.groups.length > 0) {
    return {
      version: 2,
      groups: (raw.groups as Partial<SegmentGroup>[]).map((g) => ({ ...EMPTY_GROUP, ...g })),
    };
  }
  return { version: 2, groups: [{ ...EMPTY_GROUP, ...(raw as Partial<SegmentGroup>) }] };
}

function wholeIn(n: number | undefined, min: number, max: number): boolean {
  return n !== undefined && Number.isInteger(n) && n >= min && n <= max;
}

function validEvent(e: SegmentEventCondition): boolean {
  const def = EVENT_OPTIONS.find((o) => o.value === e.event);
  if (!def || !wholeIn(e.count, 0, 1000)) return false;
  if (!e.window) return def.optionalWindow === true;
  return 'days' in e.window ? wholeIn(e.window.days, 1, def.maxDays) : true;
}

function validPurchase(p: SegmentPurchaseCondition): boolean {
  if (typeof p.value !== 'number' || !Number.isFinite(p.value) || p.value < 0) return false;
  return !p.window || !('days' in p.window) || wholeIn(p.window.days, 1, 3650);
}

/** Same cap as the server (`MAX_PREDICTED_VALUE` in predictions.ts). */
const MAX_PREDICTED_VALUE = 1_000_000;

function validPrediction(p: SegmentPredictionCondition): boolean {
  switch (p.prediction) {
    case 'next_order_within':
      return wholeIn(p.days, 1, 365);
    case 'churn_risk':
      return p.levels.length > 0;
    case 'predicted_value':
      return Number.isFinite(p.value) && p.value >= 0 && p.value <= MAX_PREDICTED_VALUE;
  }
}

function validFilter(filter: SegmentFilter): boolean {
  // The server rejects a group without conditions, so an empty OR group blocks saving and the count.
  return filter.groups.every(
    (g) =>
      groupConditionCount(g) > 0 &&
      validEngagement(g.engagement) &&
      (g.events ?? []).every(validEvent) &&
      (g.purchases ?? []).every(validPurchase) &&
      (g.predictions ?? []).every(validPrediction),
  );
}

function describeEngagement(e: SegmentEngagement): string {
  const days = `${e.days} day${e.days === 1 ? '' : 's'}`;
  return e.kind === 'clicked_within'
    ? `clicked in last ${days}`
    : `no click in ${days}, ${e.minSends ?? 3}+ mails`;
}

function validEngagement(e: SegmentEngagement | null | undefined): boolean {
  if (!e) return true;
  if (!wholeIn(e.days, 1, ENGAGEMENT_DAYS_MAX)) return false;
  return e.kind === 'clicked_within' || wholeIn(e.minSends ?? 3, 1, 100);
}

/** Clicked / has not clicked in the last N days. Human clicks only (the open pixel is off). */
function EngagementField({
  idPrefix = '',
  value,
  disabled,
  onChange,
}: {
  idPrefix?: string;
  value: SegmentEngagement | null | undefined;
  disabled: boolean;
  onChange: (next: SegmentEngagement | null) => void;
}) {
  const kind = value?.kind ?? '';
  const num = (raw: string) => (raw === '' ? NaN : Number(raw));
  return (
    <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
      <legend className="text-sm font-medium mb-1">Engagement</legend>
      <select
        aria-label={`${idPrefix}Engagement`}
        className="h-9 rounded-md border border-border bg-surface px-2 text-sm"
        disabled={disabled}
        value={kind}
        onChange={(e) => {
          const k = e.target.value;
          if (k === 'clicked_within') onChange({ kind: k, days: value?.days ?? 90 });
          else if (k === 'not_clicked_within') {
            onChange({ kind: k, days: value?.days ?? 180, minSends: value?.minSends ?? 3 });
          } else onChange(null);
        }}
      >
        <option value="">Any (no engagement condition)</option>
        <option value="clicked_within">Clicked in the last N days</option>
        <option value="not_clicked_within">Has not clicked in the last N days</option>
      </select>
      {value ? (
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={`${idPrefix}segment-engagement-days`}>Days</Label>
            <Input
              id={`${idPrefix}segment-engagement-days`}
              type="number"
              min={1}
              max={ENGAGEMENT_DAYS_MAX}
              step={1}
              disabled={disabled}
              className="w-28"
              value={Number.isNaN(value.days) ? '' : value.days}
              onChange={(e) => onChange({ ...value, days: num(e.target.value) })}
            />
          </div>
          {value.kind === 'not_clicked_within' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor={`${idPrefix}segment-engagement-min-sends`}>
                Received at least (mails)
              </Label>
              <Input
                id={`${idPrefix}segment-engagement-min-sends`}
                type="number"
                min={1}
                max={100}
                step={1}
                disabled={disabled}
                className="w-28"
                value={
                  value.minSends === undefined || Number.isNaN(value.minSends) ? '' : value.minSends
                }
                onChange={(e) => onChange({ ...value, minSends: num(e.target.value) })}
              />
            </div>
          ) : null}
        </div>
      ) : null}
      <p className="m-0 text-xs text-muted">
        Counts real clicks only; bot clicks and opens are ignored. Days: 1 to {ENGAGEMENT_DAYS_MAX}.
        {value?.kind === 'not_clicked_within'
          ? ` A contact with fewer received mails is never called unengaged. Mails older than ${LIVE_MAIL_DAYS} days are only kept as a total, so for longer windows the mail count is approximate.`
          : ''}
      </p>
    </fieldset>
  );
}

function labelName(label: string): string {
  return label in AUTO_LABEL_NAMES ? AUTO_LABEL_NAMES[label as AutoLabel] : label;
}

function describeWindow(w: SegmentEventCondition['window']): string {
  if (!w) return '';
  return 'days' in w ? ` in ${w.days}d` : ` since ${w.since}`;
}

function describeEvent(e: SegmentEventCondition): string {
  const name = EVENT_OPTIONS.find((o) => o.value === e.event)?.label.toLowerCase() ?? e.event;
  const product = e.where?.productId ?? e.where?.productName;
  const count = e.op === 'at_least' ? (e.count <= 1 ? 'at least once' : `${e.count}+ times`) : '';
  const never = e.op === 'at_most' && e.count === 0;
  const upTo = e.op === 'at_most' && e.count > 0 ? `at most ${e.count} times` : '';
  const what = never ? `never ${name}` : `${name} ${count}${upTo}`.trim();
  return `${what}${product ? ` (${product})` : ''}${describeWindow(e.window)}`;
}

function describePurchase(p: SegmentPurchaseCondition): string {
  const metric = p.metric === 'total_spent' ? 'total spent' : 'avg. order value';
  return `${metric} ${p.op === 'at_least' ? '>=' : '<='} ${p.value} EUR${describeWindow(p.window)}`;
}

function describePrediction(p: SegmentPredictionCondition): string {
  switch (p.prediction) {
    case 'next_order_within':
      return `est. next order within ${p.days} days`;
    case 'churn_risk':
      return `est. churn risk: ${p.levels.join(', ')}`;
    case 'predicted_value':
      return `est. value ${p.op === 'at_least' ? '>=' : '<='} ${p.value} EUR`;
  }
}

function describeGroup(group: SegmentGroup): string {
  const parts: string[] = [];
  if (group.labels.length > 0) parts.push(group.labels.map(labelName).join(', '));
  if (group.excludeLabels.length > 0) {
    parts.push(`excl. ${group.excludeLabels.map(labelName).join(', ')}`);
  }
  for (const f of group.fields) {
    parts.push(
      f.op === 'exists' || f.op === 'not_exists' ? `${f.key} ${f.op}` : `${f.key}=${f.value}`,
    );
  }
  if (group.unsubscribed) parts.push('unsubscribed');
  if (group.sources.length > 0) parts.push(`source: ${group.sources.join(', ')}`);
  if (group.languages.length > 0) parts.push(`language: ${group.languages.join(', ')}`);
  if (group.countries.length > 0) parts.push(`country: ${group.countries.join(', ')}`);
  if (group.excludeCountries.length > 0) {
    parts.push(`not country: ${group.excludeCountries.join(', ')}`);
  }
  if (group.engagement) parts.push(describeEngagement(group.engagement));
  if ((group.consentCategories?.length ?? 0) > 0) {
    const names = (group.consentCategories ?? []).map(
      (c) => CONSENT_OPTIONS.find((o) => o.value === c)?.label ?? c,
    );
    parts.push(`consent: ${names.join(', ')}`);
  }
  for (const e of group.events ?? []) parts.push(describeEvent(e));
  for (const p of group.purchases ?? []) parts.push(describePurchase(p));
  for (const p of group.predictions ?? []) parts.push(describePrediction(p));
  return parts.join(' · ');
}

/** A short, human summary of a filter for the list view — e.g. "dk, excl. de, unsubscribed". Groups are joined with OR. */
function describeFilter(filter: SegmentFilter | null): string {
  if (!filter) return 'Could not be read';
  const groups = filter.groups.map(describeGroup).filter((g) => g !== '');
  return groups.length > 0 ? groups.join(' OR ') : 'No conditions';
}

/**
 * Parses `?prefill=<json>` — used by `StatistikPage`'s per-source "Create
 * segment" button (P7 item 5) to open this page with a source filter
 * already picked, without either page importing the other's internals.
 * The filter may be the old flat shape or v2.
 */
function readPrefill(searchParams: URLSearchParams): SegmentDraft | null {
  const raw = searchParams.get('prefill');
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SegmentDraft>;
    if (typeof parsed.name !== 'string' || typeof parsed.filter !== 'object' || !parsed.filter) {
      return null;
    }
    return { name: parsed.name, filter: toFilterV2(parsed.filter) };
  } catch {
    return null;
  }
}

/**
 * A comma separated list of codes. The text is kept locally while typing
 * (so a trailing comma is not eaten) and only the tidied list goes up.
 */
function CodeListField({
  id,
  label,
  hint,
  casing,
  disabled,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint: string;
  casing: 'lower' | 'upper';
  disabled: boolean;
  value: string[];
  onChange: (next: string[]) => void;
}) {
  const [text, setText] = useState(value.join(', '));
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        disabled={disabled}
        value={text}
        aria-describedby={`${id}-hint`}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parseCodeList(e.target.value, casing));
        }}
      />
      <p id={`${id}-hint`} className="m-0 text-xs text-muted">
        {hint}
      </p>
    </div>
  );
}

const SELECT_CLASS = 'h-9 rounded-md border border-border bg-surface px-2 text-sm';

function numberOrNaN(raw: string): number {
  return raw === '' ? NaN : Number(raw);
}

/** Rows of "did / did not do X, N times, within N days". Adding a viewed-product event later is one more option. */
function EventsField({
  idPrefix,
  events,
  disabled,
  onChange,
}: {
  idPrefix: string;
  events: SegmentEventCondition[];
  disabled: boolean;
  onChange: (next: SegmentEventCondition[]) => void;
}) {
  const update = (i: number, patch: Partial<SegmentEventCondition>) =>
    onChange(events.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  return (
    <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
      <legend className="text-sm font-medium mb-1">Activity</legend>
      {events.map((e, i) => {
        const def = EVENT_OPTIONS.find((o) => o.value === e.event);
        const days = e.window && 'days' in e.window ? e.window.days : undefined;
        const productKind = e.where?.productId !== undefined ? 'productId' : 'productName';
        const productValue = e.where?.productId ?? e.where?.productName ?? '';
        return (
          <div key={i} className="flex flex-wrap items-end gap-2">
            <select
              aria-label={`${idPrefix}Activity ${i + 1}`}
              className={SELECT_CLASS}
              disabled={disabled}
              value={e.event}
              onChange={(ev) => {
                const next = EVENT_OPTIONS.find((o) => o.value === ev.target.value);
                const { where: keptWhere, ...rest } = e;
                const updated: SegmentEventCondition = {
                  ...rest,
                  event: ev.target.value,
                  ...(next?.product && keptWhere ? { where: keptWhere } : {}),
                  ...(next?.presence ? { count: e.op === 'at_least' ? 1 : 0 } : {}),
                };
                onChange(events.map((x, j) => (j === i ? updated : x)));
              }}
            >
              {EVENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <select
              aria-label={`${idPrefix}Activity ${i + 1} operator`}
              className={SELECT_CLASS}
              disabled={disabled}
              value={e.op}
              onChange={(ev) => {
                const op = ev.target.value as 'at_least' | 'at_most';
                update(i, { op, ...(def?.presence ? { count: op === 'at_least' ? 1 : 0 } : {}) });
              }}
            >
              <option value="at_least">at least</option>
              <option value="at_most">at most</option>
            </select>
            <Input
              aria-label={`${idPrefix}Activity ${i + 1} count`}
              type="number"
              min={0}
              step={1}
              className="w-20"
              disabled={disabled || def?.presence === true}
              value={Number.isNaN(e.count) ? '' : e.count}
              onChange={(ev) => update(i, { count: numberOrNaN(ev.target.value) })}
            />
            <span className="text-sm text-muted pb-2">times, in the last</span>
            <Input
              aria-label={`${idPrefix}Activity ${i + 1} days`}
              type="number"
              min={1}
              max={def?.maxDays}
              step={1}
              className="w-24"
              placeholder={def?.optionalWindow ? 'ever' : ''}
              disabled={disabled}
              value={days === undefined || Number.isNaN(days) ? '' : days}
              onChange={(ev) => {
                const n = numberOrNaN(ev.target.value);
                if (ev.target.value === '') {
                  const { window: _drop, ...rest } = e;
                  onChange(events.map((x, j) => (j === i ? rest : x)));
                } else update(i, { window: { days: n } });
              }}
            />
            <span className="text-sm text-muted pb-2">days</span>
            {def?.product ? (
              <>
                <select
                  aria-label={`${idPrefix}Activity ${i + 1} product match`}
                  className={SELECT_CLASS}
                  disabled={disabled}
                  value={productKind}
                  onChange={(ev) =>
                    update(i, {
                      where:
                        ev.target.value === 'productId'
                          ? { productId: productValue }
                          : { productName: productValue },
                    })
                  }
                >
                  <option value="productName">Product name</option>
                  <option value="productId">Product ID</option>
                </select>
                <Input
                  aria-label={`${idPrefix}Activity ${i + 1} product`}
                  className="w-40"
                  placeholder="any product"
                  disabled={disabled}
                  value={productValue}
                  onChange={(ev) => {
                    const v = ev.target.value;
                    if (v === '') {
                      const { where: _drop, ...rest } = e;
                      onChange(events.map((x, j) => (j === i ? rest : x)));
                    } else {
                      update(i, {
                        where: productKind === 'productId' ? { productId: v } : { productName: v },
                      });
                    }
                  }}
                />
              </>
            ) : null}
            <Button
              variant="secondary"
              disabled={disabled}
              aria-label={`${idPrefix}Remove activity ${i + 1}`}
              onClick={() => onChange(events.filter((_, j) => j !== i))}
            >
              Remove
            </Button>
          </div>
        );
      })}
      <div>
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() =>
            onChange([
              ...events,
              { event: 'clicked', op: 'at_least', count: 1, window: { days: 30 } },
            ])
          }
        >
          Add activity condition
        </Button>
      </div>
      {events.some((e) => e.event === 'opened' || e.event === 'received') ? (
        <p className="m-0 text-xs text-muted">
          Opens and received mails are only kept for {LIVE_MAIL_DAYS} days.
        </p>
      ) : null}
    </fieldset>
  );
}

function PurchasesField({
  idPrefix,
  purchases,
  disabled,
  onChange,
}: {
  idPrefix: string;
  purchases: SegmentPurchaseCondition[];
  disabled: boolean;
  onChange: (next: SegmentPurchaseCondition[]) => void;
}) {
  const update = (i: number, patch: Partial<SegmentPurchaseCondition>) =>
    onChange(purchases.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  return (
    <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
      <legend className="text-sm font-medium mb-1">Order value</legend>
      {purchases.map((p, i) => {
        const days = p.window && 'days' in p.window ? p.window.days : undefined;
        return (
          <div key={i} className="flex flex-wrap items-end gap-2">
            <select
              aria-label={`${idPrefix}Order value ${i + 1} metric`}
              className={SELECT_CLASS}
              disabled={disabled}
              value={p.metric}
              onChange={(ev) => update(i, { metric: ev.target.value as typeof p.metric })}
            >
              <option value="total_spent">Total spent</option>
              <option value="average_order_value">Average order value</option>
            </select>
            <select
              aria-label={`${idPrefix}Order value ${i + 1} operator`}
              className={SELECT_CLASS}
              disabled={disabled}
              value={p.op}
              onChange={(ev) => update(i, { op: ev.target.value as 'at_least' | 'at_most' })}
            >
              <option value="at_least">at least</option>
              <option value="at_most">at most</option>
            </select>
            <Input
              aria-label={`${idPrefix}Order value ${i + 1} amount`}
              type="number"
              min={0}
              step="any"
              className="w-28"
              disabled={disabled}
              value={Number.isNaN(p.value) ? '' : p.value}
              onChange={(ev) => update(i, { value: numberOrNaN(ev.target.value) })}
            />
            <span className="text-sm text-muted pb-2">EUR, in the last</span>
            <Input
              aria-label={`${idPrefix}Order value ${i + 1} days`}
              type="number"
              min={1}
              max={3650}
              step={1}
              className="w-24"
              placeholder="ever"
              disabled={disabled}
              value={days === undefined || Number.isNaN(days) ? '' : days}
              onChange={(ev) => {
                if (ev.target.value === '') {
                  const { window: _drop, ...rest } = p;
                  onChange(purchases.map((x, j) => (j === i ? rest : x)));
                } else update(i, { window: { days: numberOrNaN(ev.target.value) } });
              }}
            />
            <span className="text-sm text-muted pb-2">days</span>
            <Button
              variant="secondary"
              disabled={disabled}
              aria-label={`${idPrefix}Remove order value ${i + 1}`}
              onClick={() => onChange(purchases.filter((_, j) => j !== i))}
            >
              Remove
            </Button>
          </div>
        );
      })}
      <div>
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() =>
            onChange([...purchases, { metric: 'total_spent', op: 'at_least', value: 100 }])
          }
        >
          Add order value condition
        </Button>
      </div>
      <p className="m-0 text-xs text-muted">
        Amounts are net of refunds. Cancelled and failed orders do not count.
      </p>
    </fieldset>
  );
}

const PREDICTION_DEFAULTS: Record<
  SegmentPredictionCondition['prediction'],
  SegmentPredictionCondition
> = {
  next_order_within: { prediction: 'next_order_within', days: 14 },
  churn_risk: { prediction: 'churn_risk', levels: ['high'] },
  predicted_value: { prediction: 'predicted_value', op: 'at_least', value: 300 },
};

const CHURN_LEVELS: { value: ChurnRisk; label: string }[] = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

/** K6: estimates from order history (next order, churn risk, predicted value). */
function PredictionsField({
  idPrefix,
  predictions,
  disabled,
  onChange,
}: {
  idPrefix: string;
  predictions: SegmentPredictionCondition[];
  disabled: boolean;
  onChange: (next: SegmentPredictionCondition[]) => void;
}) {
  const replace = (i: number, next: SegmentPredictionCondition) =>
    onChange(predictions.map((p, j) => (j === i ? next : p)));
  return (
    <fieldset className="flex flex-col gap-2 border-0 p-0 m-0">
      <legend className="text-sm font-medium mb-1">Estimates</legend>
      {predictions.map((p, i) => (
        <div key={i} className="flex flex-wrap items-end gap-2">
          <select
            aria-label={`${idPrefix}Estimate ${i + 1} kind`}
            className={SELECT_CLASS}
            disabled={disabled}
            value={p.prediction}
            onChange={(ev) =>
              replace(
                i,
                PREDICTION_DEFAULTS[ev.target.value as SegmentPredictionCondition['prediction']],
              )
            }
          >
            <option value="next_order_within">Next order due in the next</option>
            <option value="churn_risk">Churn risk is</option>
            <option value="predicted_value">Predicted value is</option>
          </select>
          {p.prediction === 'next_order_within' ? (
            <>
              <Input
                aria-label={`${idPrefix}Estimate ${i + 1} days`}
                type="number"
                min={1}
                max={365}
                step={1}
                className="w-24"
                disabled={disabled}
                value={Number.isNaN(p.days) ? '' : p.days}
                onChange={(ev) => replace(i, { ...p, days: numberOrNaN(ev.target.value) })}
              />
              <span className="text-sm text-muted pb-2">days</span>
            </>
          ) : null}
          {p.prediction === 'churn_risk' ? (
            <div
              className="flex gap-3 pb-2"
              role="group"
              aria-label={`${idPrefix}Estimate ${i + 1} levels`}
            >
              {CHURN_LEVELS.map((l) => (
                <label key={l.value} className="flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    disabled={disabled}
                    checked={p.levels.includes(l.value)}
                    onChange={(ev) =>
                      replace(i, {
                        ...p,
                        levels: ev.target.checked
                          ? CHURN_LEVELS.map((x) => x.value).filter(
                              (v) => v === l.value || p.levels.includes(v),
                            )
                          : p.levels.filter((v) => v !== l.value),
                      })
                    }
                  />
                  {l.label}
                </label>
              ))}
            </div>
          ) : null}
          {p.prediction === 'predicted_value' ? (
            <>
              <select
                aria-label={`${idPrefix}Estimate ${i + 1} operator`}
                className={SELECT_CLASS}
                disabled={disabled}
                value={p.op}
                onChange={(ev) =>
                  replace(i, { ...p, op: ev.target.value as 'at_least' | 'at_most' })
                }
              >
                <option value="at_least">at least</option>
                <option value="at_most">at most</option>
              </select>
              <Input
                aria-label={`${idPrefix}Estimate ${i + 1} amount`}
                type="number"
                min={0}
                max={MAX_PREDICTED_VALUE}
                step="any"
                className="w-28"
                disabled={disabled}
                value={Number.isNaN(p.value) ? '' : p.value}
                onChange={(ev) => replace(i, { ...p, value: numberOrNaN(ev.target.value) })}
              />
              <span className="text-sm text-muted pb-2">EUR</span>
            </>
          ) : null}
          <Button
            variant="secondary"
            disabled={disabled}
            aria-label={`${idPrefix}Remove estimate ${i + 1}`}
            onClick={() => onChange(predictions.filter((_, j) => j !== i))}
          >
            Remove
          </Button>
        </div>
      ))}
      <div>
        <Button
          variant="secondary"
          disabled={disabled}
          onClick={() => onChange([...predictions, PREDICTION_DEFAULTS.next_order_within])}
        >
          Add estimate condition
        </Button>
      </div>
      <p className="m-0 text-xs text-muted">
        Estimates from each buyer&apos;s order rhythm with simple rules, not a promise. A one-order
        buyer uses the shop&apos;s typical rhythm once 20 customers have reordered; contacts without
        enough order history never match. Predicted value is what they have spent plus the expected
        spend over the next year.
      </p>
    </fieldset>
  );
}

/** One AND-group of conditions. Group 1 keeps the plain ids and labels; later groups add a suffix. */
function GroupEditor({
  index,
  group,
  labelOptions,
  disabled,
  onChange,
}: {
  index: number;
  group: SegmentGroup;
  labelOptions: { value: string; label: string }[];
  disabled: boolean;
  onChange: (next: SegmentGroup) => void;
}) {
  const sfx = index === 0 ? '' : ` (group ${index + 1})`;
  const idp = index === 0 ? '' : `g${index + 1}-`;
  const set = (patch: Partial<SegmentGroup>) => onChange({ ...group, ...patch });
  return (
    <div className="flex flex-col gap-4">
      <CheckboxGroup
        legend={`Must have all of these tags${sfx}`}
        options={labelOptions}
        selected={group.labels}
        disabled={disabled}
        onChange={(labels) => set({ labels })}
        empty="No tags yet."
      />
      <CheckboxGroup
        legend={`Must not have any of these tags${sfx}`}
        options={labelOptions}
        selected={group.excludeLabels}
        disabled={disabled}
        onChange={(excludeLabels) => set({ excludeLabels })}
        empty="No tags yet."
      />
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          disabled={disabled}
          checked={group.unsubscribed}
          onChange={(e) => set({ unsubscribed: e.target.checked })}
        />
        Only contacts who have unsubscribed (Mailchimp&apos;s &quot;Afmeldte&quot;)
      </label>
      <CodeListField
        id={`${idp}segment-languages`}
        label={`Language is one of${sfx}`}
        hint="Two-letter codes, comma separated, e.g. da, de. A contact with no known language never matches."
        casing="lower"
        disabled={disabled}
        value={group.languages}
        onChange={(languages) => set({ languages })}
      />
      <CodeListField
        id={`${idp}segment-countries`}
        label={`Country is one of${sfx}`}
        hint="ISO 3166-1 codes, comma separated, e.g. DK, DE. A contact with no known country never matches."
        casing="upper"
        disabled={disabled}
        value={group.countries}
        onChange={(countries) => set({ countries })}
      />
      <CodeListField
        id={`${idp}segment-exclude-countries`}
        label={`Country is none of${sfx}`}
        hint="A contact with no known country is kept, because it cannot be shown to be in that country."
        casing="upper"
        disabled={disabled}
        value={group.excludeCountries}
        onChange={(excludeCountries) => set({ excludeCountries })}
      />
      <CheckboxGroup
        legend={`Consent evidence is one of${sfx}`}
        options={CONSENT_OPTIONS}
        selected={group.consentCategories ?? []}
        disabled={disabled}
        onChange={(picked) => set({ consentCategories: picked as SegmentConsentCategory[] })}
        empty=""
      />
      <EngagementField
        idPrefix={idp}
        value={group.engagement}
        disabled={disabled}
        onChange={(engagement) => set({ engagement })}
      />
      <EventsField
        idPrefix={idp}
        events={group.events ?? []}
        disabled={disabled}
        onChange={(events) => set({ events })}
      />
      <PurchasesField
        idPrefix={idp}
        purchases={group.purchases ?? []}
        disabled={disabled}
        onChange={(purchases) => set({ purchases })}
      />
      <PredictionsField
        idPrefix={idp}
        predictions={group.predictions ?? []}
        disabled={disabled}
        onChange={(predictions) => set({ predictions })}
      />
      {group.sources.length > 0 ? (
        <p className="m-0 text-xs text-muted">
          This segment also matches signup source{group.sources.length === 1 ? '' : 's'}{' '}
          {group.sources.join(', ')} (set from Statistics &rarr; Signup sources); it is kept as-is
          when you save.
        </p>
      ) : null}
      {group.fields.length > 0 ? (
        <p className="m-0 text-xs text-muted">
          This segment also has {group.fields.length} field condition
          {group.fields.length === 1 ? '' : 's'} that can&apos;t be edited here yet (contact-field
          conditions are set via the API); they are kept as-is when you save.
        </p>
      ) : null}
    </div>
  );
}

/**
 * Live count for the filter being built. Debounced and only asked when the
 * filter is valid: each call scans the contacts table on the server.
 */
function useLiveCount(filter: SegmentFilter, enabled: boolean) {
  const [settled, setSettled] = useState(filter);
  useEffect(() => {
    const t = setTimeout(() => setSettled(filter), 600);
    return () => clearTimeout(t);
  }, [filter]);
  const ready = enabled && conditionCount(settled) > 0 && validFilter(settled);
  return useQuery({
    queryKey: ['segment-estimate', settled],
    enabled: ready,
    staleTime: 60_000,
    retry: false,
    queryFn: () =>
      apiFetch<SegmentEstimate>('/segments/estimate', {
        method: 'POST',
        body: { filter: settled },
      }),
  });
}

function SegmentForm({
  draft,
  labelOptions,
  isAdmin,
  pending,
  error,
  onChange,
  onCancel,
  onSave,
}: {
  draft: SegmentDraft;
  labelOptions: { value: string; label: string }[];
  isAdmin: boolean;
  pending: boolean;
  error: string | null;
  onChange: (next: SegmentDraft) => void;
  onCancel: () => void;
  onSave: () => void;
}) {
  const disabled = !isAdmin || pending;
  const groups = draft.filter.groups;
  const total = conditionCount(draft.filter);
  const heavy = heavyCount(draft.filter);
  const overCap = total > MAX_CONDITIONS || heavy > MAX_HEAVY_CONDITIONS;
  const live = useLiveCount(draft.filter, isAdmin && !overCap);
  // Stable keys per group: the list fields keep their own text, so an index key would show the
  // removed group's text in the group that moves up.
  const nextGroupKey = useRef(groups.length);
  const [groupKeys, setGroupKeys] = useState(() => groups.map((_, i) => i));
  const setGroups = (next: SegmentGroup[]) =>
    onChange({ ...draft, filter: { version: 2, groups: next } });
  const addGroup = () => {
    setGroupKeys([...groupKeys, nextGroupKey.current++]);
    setGroups([...groups, EMPTY_GROUP]);
  };
  const removeGroup = (i: number) => {
    setGroupKeys(groupKeys.filter((_, j) => j !== i));
    setGroups(groups.filter((_, j) => j !== i));
  };
  return (
    <>
      <SheetBody>
        {!isAdmin ? (
          <Alert>You have read-only access. Contact an administrator to change segments.</Alert>
        ) : null}
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="segment-name">Name</Label>
          <Input
            id="segment-name"
            required
            maxLength={100}
            disabled={disabled}
            value={draft.name}
            onChange={(e) => onChange({ ...draft, name: e.target.value })}
          />
        </div>
        {groups.map((group, i) => (
          <div key={groupKeys[i] ?? `extra-${i}`} className="flex flex-col gap-4">
            {i > 0 ? (
              <div className="flex items-center justify-between gap-2 border-t border-border-soft pt-4">
                <span className="text-sm font-medium">OR group {i + 1}</span>
                <Button
                  variant="secondary"
                  disabled={disabled}
                  aria-label={`Remove group ${i + 1}`}
                  onClick={() => removeGroup(i)}
                >
                  Remove group
                </Button>
              </div>
            ) : null}
            <GroupEditor
              index={i}
              group={group}
              labelOptions={labelOptions}
              disabled={disabled}
              onChange={(next) => setGroups(groups.map((g, j) => (j === i ? next : g)))}
            />
          </div>
        ))}
        {isAdmin ? (
          <div className="flex flex-col gap-1">
            <div>
              <Button
                variant="secondary"
                disabled={disabled || groups.length >= MAX_GROUPS}
                onClick={addGroup}
              >
                Add OR group
              </Button>
            </div>
            <p className="m-0 text-xs text-muted">
              A contact matches if it meets all conditions of at least one group. Up to {MAX_GROUPS}{' '}
              groups, {MAX_CONDITIONS} conditions in total, {MAX_HEAVY_CONDITIONS} of them activity,
              order value or estimates.
            </p>
          </div>
        ) : null}
        {overCap ? (
          <Alert>
            Too many conditions: at most {MAX_CONDITIONS} in total and {MAX_HEAVY_CONDITIONS}{' '}
            activity, order value or estimate conditions.
          </Alert>
        ) : null}
        <div role="status" aria-live="polite" className="text-sm" data-testid="live-count">
          {live.data ? (
            <>
              <strong>{formatNumber(live.data.matching)}</strong> contacts match,{' '}
              <strong>{formatNumber(live.data.reachable)}</strong> can be mailed right now.
            </>
          ) : live.isError ? (
            <span className="text-muted">The count is not available for this filter.</span>
          ) : live.isFetching ? (
            <span className="text-muted">Counting…</span>
          ) : (
            <span className="text-muted">Add a condition to see how many contacts match.</span>
          )}
        </div>
        {error ? <Alert>{error}</Alert> : null}
      </SheetBody>
      <SheetFooter>
        <Button variant="secondary" className="flex-grow" onClick={onCancel}>
          Cancel
        </Button>
        {isAdmin ? (
          <Button
            className="flex-grow"
            disabled={
              pending ||
              draft.name.trim() === '' ||
              total === 0 ||
              overCap ||
              !validFilter(draft.filter)
            }
            onClick={onSave}
          >
            {pending ? 'Saving…' : 'Save segment'}
          </Button>
        ) : null}
      </SheetFooter>
    </>
  );
}

/** The library by goal: each preset creates an ordinary, editable segment. */
function SegmentLibrary({
  pending,
  onCreate,
}: {
  pending: boolean;
  onCreate: (name: string, filter: SegmentFilter) => void;
}) {
  const library = useQuery({
    queryKey: ['segment-library'],
    staleTime: Infinity,
    queryFn: () => apiFetch<SegmentLibraryResponse>('/segments/library'),
  });
  const [goal, setGoal] = useState<string | null>(null);
  const data = library.data;
  if (!data) return null;
  const active = goal ?? data.goals[0]?.goal ?? null;
  const presets = data.presets.filter((p) => p.goal === active);
  return (
    <Card className="p-4 max-w-[900px] flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">Start from the library:</span>
        {data.goals.map((g) => (
          <Button
            key={g.goal}
            variant={g.goal === active ? 'primary' : 'secondary'}
            aria-pressed={g.goal === active}
            onClick={() => setGoal(g.goal)}
          >
            {g.title}
          </Button>
        ))}
      </div>
      <ul className="m-0 p-0 list-none flex flex-col gap-2">
        {presets.map((p) => (
          <li key={p.key} className="flex items-center justify-between gap-3">
            <div>
              <div className="text-sm font-medium">{p.name}</div>
              <div className="text-xs text-muted">{p.description}</div>
            </div>
            <Button
              variant="secondary"
              disabled={pending}
              aria-label={`Create segment ${p.name}`}
              onClick={() => onCreate(p.name, p.filter)}
            >
              Create
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}

/**
 * Settings → Segments (P7 item 3, `docs/api-ui.md` "Segmenter"). The
 * backend and `/api/segments` already existed for campaign audiences
 * (`AudiencePicker.tsx`); this page is the missing admin UI to create,
 * edit and archive them by hand, so staff can recreate NoPain's
 * Mailchimp segments (DK, DE, B2B, "All except Germany", Inactive,
 * Unsubscribed) without touching the API directly.
 */
export function SegmenterPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showArchived, setShowArchived] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState<SegmentDraft>({ name: '', filter: EMPTY_FILTER });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['segments', { showArchived }],
    queryFn: () =>
      apiFetch<{ items: Segment[] }>(`/segments${showArchived ? '?includeArchived=1' : ''}`),
  });
  const labels = useQuery({
    queryKey: ['labels'],
    queryFn: () => apiFetch<LabelsOverviewResponse>('/labels'),
  });
  const labelOptions = (labels.data?.counts ?? [])
    .map((c) => ({ value: c.label, label: labelName(c.label) }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['segments'] });

  // A prefilled draft (from StatistikPage's "Create segment" button, P7
  // item 5) opens the create panel once, then is consumed from the URL so
  // navigating away and back doesn't reopen it.
  useEffect(() => {
    const prefill = readPrefill(searchParams);
    if (!prefill) return;
    setDraft(prefill);
    setCreating(true);
    const next = new URLSearchParams(searchParams);
    next.delete('prefill');
    setSearchParams(next, { replace: true });
    // Removing `prefill` above makes `readPrefill` return null on the
    // re-run this causes, so depending on `searchParams` is safe and the
    // effect only ever opens the create panel once.
  }, [searchParams, setSearchParams]);

  const createMutation = useMutation({
    mutationFn: (input: SegmentDraft) =>
      apiFetch<Segment>('/segments', {
        method: 'POST',
        body: { name: input.name, filter: input.filter },
      }),
    onSuccess: (segment) => {
      setNotice(`The segment "${segment.name}" has been created.`);
      setCreating(false);
      invalidate();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: SegmentDraft }) =>
      apiFetch<Segment>(`/segments/${id}`, {
        method: 'PATCH',
        body: { name: input.name, filter: input.filter },
      }),
    onSuccess: (segment) => {
      setNotice(`The segment "${segment.name}" has been saved.`);
      setEditingId(null);
      invalidate();
    },
  });

  const archiveMutation = useMutation({
    mutationFn: ({ id, archived }: { id: string; archived: boolean }) =>
      apiFetch<Segment>(`/segments/${id}`, { method: 'PATCH', body: { archived } }),
    onSuccess: (segment) => {
      setNotice(
        segment.archived
          ? `"${segment.name}" has been archived.`
          : `"${segment.name}" has been restored.`,
      );
      invalidate();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (segment: Segment) =>
      apiFetch<{ result: 'deleted' | 'archived' }>(`/segments/${segment.id}`, {
        method: 'DELETE',
      }),
    onSuccess: (res, segment) => {
      setNotice(
        res.result === 'deleted'
          ? `"${segment.name}" has been deleted.`
          : `"${segment.name}" is used by a campaign and has been archived instead.`,
      );
      invalidate();
    },
  });

  const items = data?.items ?? [];
  const editing = editingId ? (items.find((s) => s.id === editingId) ?? null) : null;
  const sheetOpen = creating || editing !== null;

  function openCreate() {
    setDraft({ name: '', filter: EMPTY_FILTER });
    setEditingId(null);
    setCreating(true);
  }

  function openEdit(segment: Segment) {
    setDraft({ name: segment.name, filter: toFilterV2(segment.filter) });
    setCreating(false);
    setEditingId(segment.id);
  }

  function closeSheet() {
    setCreating(false);
    setEditingId(null);
  }

  const saveError = apiErrorMessage(
    creating ? createMutation.error : updateMutation.error,
    'The segment could not be saved.',
  );
  const rowError =
    (creating ? null : apiErrorMessage(createMutation.error, 'The segment could not be saved.')) ??
    apiErrorMessage(archiveMutation.error, 'The segment could not be changed.') ??
    apiErrorMessage(deleteMutation.error, 'The segment could not be deleted.');

  return (
    <>
      <PageHeader
        title="Segments"
        badge={<MockBadge />}
        description="Groups of contacts that update themselves. A segment a campaign has used is archived instead of deleted, so the history is preserved."
        actions={isAdmin ? <Button onClick={openCreate}>New segment</Button> : null}
      />

      <div className="flex items-center justify-end gap-3">
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          Show archived segments
        </label>
      </div>

      {isAdmin ? (
        <SegmentLibrary
          pending={createMutation.isPending}
          onCreate={(name, filter) => createMutation.mutate({ name, filter })}
        />
      ) : null}
      {!isAdmin ? (
        <Alert>You have read-only access. Contact an administrator to change segments.</Alert>
      ) : null}
      {notice ? (
        <div role="status" className="text-success-text text-sm">
          {notice}
        </div>
      ) : null}
      {rowError ? <Alert>{rowError}</Alert> : null}

      {isLoading ? <div className="text-muted">Loading segments…</div> : null}
      {isError ? <Alert>The segments could not be loaded.</Alert> : null}
      {!isLoading && !isError && items.length === 0 ? (
        <EmptyState
          icon={<Filter />}
          title="No segments yet"
          description="A segment picks contacts by rules, for example everyone who bought in the last 90 days. New contacts join by themselves."
          examples={['Bought in the last 90 days', 'Opened but never bought', 'Customer tag: VIP']}
          action={isAdmin ? <Button onClick={openCreate}>New segment</Button> : undefined}
        />
      ) : null}

      {items.length > 0 ? (
        <Card className="p-0 overflow-hidden max-w-[900px]">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="text-left text-xs text-muted uppercase tracking-wide">
                <th className="px-4 py-2.5 font-medium">Name</th>
                <th className="px-4 py-2.5 font-medium">Conditions</th>
                <th className="px-4 py-2.5 font-medium text-right">Campaigns</th>
                <th className="px-4 py-2.5 font-medium text-right">Created</th>
                <th className="px-4 py-2.5 font-medium" />
              </tr>
            </thead>
            <tbody>
              {items.map((segment) => (
                <tr key={segment.id} className="border-t border-border-soft">
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => openEdit(segment)}
                      className="text-left font-medium"
                    >
                      {segment.name}
                    </button>
                    {segment.archived ? (
                      <span className="ml-2">
                        <StatusBadge tone="neutral" icon="◌">
                          Archived
                        </StatusBadge>
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3 text-muted">{describeFilter(segment.filter)}</td>
                  <td className="px-4 py-3 text-right font-mono">
                    {formatNumber(segment.campaignCount)}
                  </td>
                  <td className="px-4 py-3 text-right text-muted">
                    {formatDate(segment.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin ? (
                      <div className="flex gap-2 justify-end">
                        <Button
                          variant="secondary"
                          aria-label={`${segment.archived ? 'Restore' : 'Archive'} ${segment.name}`}
                          disabled={archiveMutation.isPending}
                          onClick={() =>
                            archiveMutation.mutate({
                              id: segment.id,
                              archived: !segment.archived,
                            })
                          }
                        >
                          {segment.archived ? 'Restore' : 'Archive'}
                        </Button>
                        <Button
                          variant="secondary"
                          aria-label={`Delete ${segment.name}`}
                          disabled={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(segment)}
                        >
                          Delete
                        </Button>
                      </div>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      ) : null}

      <Sheet
        open={sheetOpen}
        onOpenChange={(open) => {
          if (!open) closeSheet();
        }}
      >
        {sheetOpen ? (
          <SheetContent
            eyebrow={creating ? 'New segment' : 'Edit segment'}
            title={creating ? 'New segment' : (editing?.name ?? '')}
            description={
              creating
                ? 'A segment matches contacts by tags, language, country, consent, activity, order value or engagement. Add an OR group to match one set of conditions or another.'
                : `Edit the conditions for "${editing?.name ?? ''}".`
            }
          >
            <SegmentForm
              key={creating ? 'new' : (editingId ?? '')}
              draft={draft}
              labelOptions={labelOptions}
              isAdmin={isAdmin}
              pending={createMutation.isPending || updateMutation.isPending}
              error={saveError}
              onChange={setDraft}
              onCancel={closeSheet}
              onSave={() => {
                if (creating) createMutation.mutate(draft);
                else if (editingId) updateMutation.mutate({ id: editingId, input: draft });
              }}
            />
          </SheetContent>
        ) : null}
      </Sheet>
    </>
  );
}
